import React, { useState } from 'react';
import { Linkedin, Facebook, Instagram } from 'lucide-react';
import NewsletterModal from './NewsletterModal';

const Footer = ({ onNavigate }) => {
  const [showModal, setShowModal] = useState(false);
  const [subscribedEmail, setSubscribedEmail] = useState('');
  console.log('🔍 Footer component rendering');
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFooterLinkClick = (section) => {
    // Navigate to home first
    if (window.location.pathname !== '/') {
      window.location.href = '/#' + section;
    } else {
      // Already on home, just scroll
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-slate-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand Section */}
          <div>
            <div className="flex items-center mb-4">
              <img 
                src="/logo-footer.png" 
                alt="Create 3D Works" 
                className="h-12 w-auto object-contain"
              />
            </div>
            <p className="text-slate-400 leading-relaxed mb-4">
              Leading the future of 3D manufacturing with precision engineering and cutting-edge technology.
            </p>
            
            {/* Social Media Icons */}
            <div className="flex items-center space-x-4 mb-6">
              <a 
                href="https://www.linkedin.com/company/create-3d-works/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://www.facebook.com/create3dworks" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a 
                href="https://instagram.com/create3dworks" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-white transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
            
            {/* Newsletter Signup */}
            <div>
              <h5 className="font-semibold text-white mb-3">Stay Updated</h5>
              <p className="text-slate-400 text-sm mb-3">Get the latest on 3D printing technology and exclusive offers.</p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const email = e.target.email.value;
                
                try {
                  const response = await fetch(window.location.origin + '/api/newsletter/subscribe', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email })
                  });
                  
                  const data = await response.json();
                  
                  if (data.success) {
                    // Show modal for additional info
                    setSubscribedEmail(email);
                    setShowModal(true);
                    e.target.reset();
                  } else {
                    alert(data.message || 'Subscription failed. Please try again.');
                  }
                } catch (err) {
                  console.error('Newsletter signup error:', err);
                  alert('Subscription failed. Please try again.');
                }
              }} className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  placeholder="Your email"
                  required
                  className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all text-sm whitespace-nowrap"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-white mb-4">Services</h4>
            <ul className="space-y-2 text-slate-400">
              <li>
                <button 
                  onClick={() => {
                    if (window.location.pathname !== '/services') {
                      window.location.href = '/services#3d-printing';
                    } else {
                      const element = document.getElementById('3d-printing');
                      if (element) {
                        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                        const offsetPosition = elementPosition - 100;
                        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                      }
                    }
                  }}
                  className="hover:text-blue-400 transition-colors text-left"
                >
                  3D File Printing
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    if (window.location.pathname !== '/services') {
                      window.location.href = '/services#conversion';
                    } else {
                      const element = document.getElementById('conversion');
                      if (element) {
                        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                        const offsetPosition = elementPosition - 100;
                        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                      }
                    }
                  }}
                  className="hover:text-blue-400 transition-colors text-left"
                >
                  2D to 3D Conversion
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    if (window.location.pathname !== '/services') {
                      window.location.href = '/services#design';
                    } else {
                      const element = document.getElementById('design');
                      if (element) {
                        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                        const offsetPosition = elementPosition - 100;
                        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                      }
                    }
                  }}
                  className="hover:text-blue-400 transition-colors text-left"
                >
                  Design Services
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    if (window.location.pathname !== '/services') {
                      window.location.href = '/services#optimization';
                    } else {
                      const element = document.getElementById('optimization');
                      if (element) {
                        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                        const offsetPosition = elementPosition - 100;
                        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                      }
                    }
                  }}
                  className="hover:text-blue-400 transition-colors text-left"
                >
                  Product Optimization
                </button>
              </li>
              <li>
                <button 
                  onClick={() => {
                    if (window.location.pathname !== '/services') {
                      window.location.href = '/services#renders';
                    } else {
                      const element = document.getElementById('renders');
                      if (element) {
                        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                        const offsetPosition = elementPosition - 100;
                        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                      }
                    }
                  }}
                  className="hover:text-blue-400 transition-colors text-left"
                >
                  Model & Product Renders
                </button>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-bold text-white mb-4">Company</h4>
            <ul className="space-y-2 text-slate-400">
              <li><a href="/about" className="hover:text-blue-400 transition-colors">About Us</a></li>
              <li><a href="/services" className="hover:text-blue-400 transition-colors">Services</a></li>
              <li><button onClick={() => handleFooterLinkClick('contact')} className="hover:text-blue-400 transition-colors text-left">Contact</button></li>
              <li><a href="/terms-of-service" className="hover:text-blue-400 transition-colors">Terms of Service</a></li>
              <li><a href="/licensing-policy" className="hover:text-blue-400 transition-colors">Licensing</a></li>
              <li><a href="/privacy-policy" className="hover:text-blue-400 transition-colors">Privacy Policy</a></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-bold text-white mb-4">Resources</h4>
            <ul className="space-y-2 text-slate-400">
              <li><a href="/faq" className="hover:text-blue-400 transition-colors">FAQ</a></li>
              <li><a href="/file-formats" className="hover:text-blue-400 transition-colors">File Formats</a></li>
              <li><a href="/material-guide" className="hover:text-blue-400 transition-colors">Material Guide</a></li>
              <li><a href="/shipping-returns" className="hover:text-blue-400 transition-colors">Shipping & Returns</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-slate-400">
              <p>&copy; 2026 <span className="brand-name"><span className="brand-name-bold">CREATE 3D</span> Works</span>. All rights reserved.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Newsletter Modal */}
      <NewsletterModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        email={subscribedEmail}
        onSave={async (additionalInfo) => {
          try {
            const response = await fetch(window.location.origin + '/api/newsletter/update-info', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                email: subscribedEmail,
                ...additionalInfo
              })
            });
            
            const data = await response.json();
            
            if (data.success) {
              alert('Thank you! Your information has been saved.');
              setShowModal(false);
            } else {
              alert('Failed to save information. Please try again.');
            }
          } catch (err) {
            console.error('Error saving additional info:', err);
            alert('Failed to save information. Please try again.');
          }
        }}
      />
    </footer>
  );
};

export default Footer;
